import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/app/hooks";
import { setSelectedUser } from "../../redux/features/adminSlice";

interface ClickableUserNameProps {
  user: any;
  userType: "VENDOR" | "BUYER";
  displayName?: string;
}

const isPlaceholderName = (value: string) => {
  const v = value.trim();
  if (!v) return true;
  const lower = v.toLowerCase();
  return (
    v === "-" ||
    v === "—" ||
    v === "–" ||
    lower === "n/a" ||
    lower === "na" ||
    lower === "null" ||
    lower === "undefined"
  );
};

const resolveDisplayName = (user: any, displayName?: string) => {
  if (displayName && !isPlaceholderName(displayName)) return displayName.trim();

  const first = user?.firstName != null ? String(user.firstName).trim() : "";
  const last = user?.lastName != null ? String(user.lastName).trim() : "";
  const composed = [first, last]
    .filter((part) => part && !isPlaceholderName(part))
    .join(" ");
  if (composed) return composed;

  const full = user?.fullName != null ? String(user.fullName).trim() : "";
  if (full && !isPlaceholderName(full)) return full;

  return "N/A";
};

const ClickableUserName: React.FC<ClickableUserNameProps> = ({
  user,
  userType,
  displayName,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const name = resolveDisplayName(user, displayName);

  if (!user?.id) {
    return <span>{name}</span>;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setSelectedUser(user));

    // Always route based on the column context (userType prop)
    // so the admin sees the page matching the table they clicked from
    if (userType === "VENDOR") {
      navigate(`/user-management/vendor/${user.id}`);
    } else {
      navigate(`/user-management/buyer/${user.id}`);
    }
  };

  return (
    <span
      onClick={handleClick}
      className="text-purple-600 hover:text-purple-800 hover:underline cursor-pointer font-medium"
      title={`View ${userType.toLowerCase()} details`}
    >
      {name}
    </span>
  );
};

/**
 * A simpler clickable user name that uses store.dispatch directly.
 * Use this in utility functions (e.g. tableUtils.tsx) where hooks are not available.
 */
export const ClickableUserNameInline: React.FC<ClickableUserNameProps> = ({
  user,
  userType,
  displayName,
}) => {
  const name = resolveDisplayName(user, displayName);

  if (!user?.id) {
    return <span>{name}</span>;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Dynamically import store to avoid circular dependencies
    import("../../redux/app/store").then(({ store }) => {
      store.dispatch(setSelectedUser(user));
    });
    const path =
      userType === "VENDOR"
        ? `/user-management/vendor/${user.id}`
        : `/user-management/buyer/${user.id}`;
    window.location.href = path;
  };

  return (
    <span
      onClick={handleClick}
      className="text-purple-600 hover:text-purple-800 hover:underline cursor-pointer font-medium"
      title={`View ${userType.toLowerCase()} details`}
    >
      {name}
    </span>
  );
};

export default ClickableUserName;
